import React, { RefObject } from 'react';
import { ValueLabelOption } from './ValueLabelOption';
import { Autocomplete } from '@mui/lab';
import {
  Checkbox,
  InputAdornment,
  OutlinedInputProps,
  Paper,
  Theme,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    noBorder: {
      border: 'none',
    },
    autoWidth: {
      width: 'auto !important',
    },
    readOnlyInput: {
      backgroundColor: '#F3F3F3',
    },
    noMargin: {
      '& .MuiFormControl-marginDense': {
        margin: 0,
      },
    },
  })
);

export interface AutoCompleteValueLabelPrimitiveProps<
  T extends readonly ValueLabelOption[]
> {
  name: string;
  label: string;
  variant?: 'outlined' | 'filled' | 'standard';
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  placeholder?: string;
  onChangeHandler?: Function;
  onBlurHandler?: Function;
  options: T;
  renderCheckboxes?: boolean;
  multiple?: boolean;
  disabledOptions?: Array<T[number]['value']>;
  disableClearable?: boolean;
  ref?: RefObject<HTMLInputElement>;
  InputRef?: any;
  value?: T[number]['value'] | Array<T[number]['value']>;
  error?: { message?: string };
  style?: object;
  isTableCellInput?: boolean;
}
/**
 * A styled autocomplete that stores only the string value as part of state. Useful for not translating string entries to the value label format for the
 * other Autocomplete component.
 * @param param0
 * @returns
 */
export const AutoCompleteValueLabelPrimitive = <
  T extends readonly ValueLabelOption[]
>({
  name,
  label,
  variant = 'outlined',
  required = false,
  disabled = false,
  size = 'medium',
  placeholder = '',
  onChangeHandler,
  onBlurHandler = () => {},
  options,
  renderCheckboxes = false,
  multiple = false,
  disabledOptions = [],
  disableClearable = false,
  ref = null,
  InputRef = null,
  value,
  error,
  style,
  isTableCellInput = false,
}: AutoCompleteValueLabelPrimitiveProps<T>) => {
  const classes = useStyles();
  const strippedOptions = options.map(o => o.value) as T[number]['value'][];
  const optionLabelMap = Object.fromEntries(
    options.map(o => [o.value, o.label])
  ) as Record<T[number]['value'], string>;

  return (
    <Autocomplete<T[number]['value'], boolean, boolean, boolean>
      className={isTableCellInput ? classes.noMargin : null}
      style={style}
      id={name}
      value={value}
      ref={ref}
      placeholder={placeholder}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disableClearable={disableClearable}
      options={strippedOptions}
      getOptionLabel={(option: T[number]['value']) => {
        return optionLabelMap[option];
      }}
      disabled={disabled}
      getOptionDisabled={(option: T[number]['value']) => {
        return disabledOptions.some(item => item === option);
      }}
      size={size}
      onBlur={() => {
        onBlurHandler();
      }}
      onChange={(_event, value, reason) => {
        if (onChangeHandler) {
          onChangeHandler(value, reason);
        }
      }}
      fullWidth
      renderInput={params => {
        const inputProps: OutlinedInputProps = { ...params.InputProps };
        if (params.disabled) {
          inputProps.endAdornment = (
            <InputAdornment
              position="end"
              style={{ backgroundColor: 'inherit' }}
            >
              <Lock />
            </InputAdornment>
          );
        }
        if (isTableCellInput) {
          inputProps.classes = {
            ...inputProps.classes,
            notchedOutline: classes.noBorder,
            input: classes.autoWidth,
          };
        }
        return (
          <TextField
            {...params}
            inputRef={InputRef}
            label={label}
            value={value}
            variant={variant}
            required={required}
            error={!!error}
            helperText={error ? error.message : null}
            InputProps={inputProps}
            style={{ textOverflow: 'ellipsis' }}
            margin={isTableCellInput ? 'dense' : 'none'}
          />
        );
      }}
      renderOption={(props, option: T[number]['value'], { selected }) => (
        <li {...props}>
          {renderCheckboxes && (
            <Checkbox
              color="primary"
              inputProps={{ 'aria-label': 'primary checkbox' }}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={!!selected}
            />
          )}
          {optionLabelMap[option]}
        </li>
      )}
      PaperComponent={({ children }) => (
        <Paper style={{ background: '#fff' }}>{children}</Paper>
      )}
    />
  );
};
